import React, { useMemo, useState } from "react";
import { formatInt } from "../shared/formatters";

export interface AgencyEngagementRow {
  id: string;
  label: string;
  activeUsers: number;
  totalUsers: number;
}

interface AgencyEngagementProps {
  rows: AgencyEngagementRow[];
  title?: string;
  subtitle?: string;
}

type SortKey = "label" | "active" | "total" | "engagement";

const engagementColor = (pct: number): string => {
  if (pct >= 70) return "#16a34a";
  if (pct >= 40) return "#ca8a04";
  return "#dc2626";
};

const enrich = (r: AgencyEngagementRow) => {
  const total = r.totalUsers || 0;
  const active = r.activeUsers || 0;
  const dormant = Math.max(0, total - active);
  const pct = total > 0 ? Math.round((active / total) * 100) : 0;
  return { ...r, dormant, pct };
};

const AgencyEngagement: React.FC<AgencyEngagementProps> = ({
  rows,
  title = "Agency engagement",
  subtitle,
}) => {
  // Default sort: lowest engagement % first - that's the actionable signal
  // (these agencies have seats not pulling their weight).
  const [sortKey, setSortKey] = useState<SortKey>("engagement");
  const [sortAsc, setSortAsc] = useState(true);

  const enriched = useMemo(() => rows.map(enrich), [rows]);

  const sorted = useMemo(() => {
    const out = [...enriched];
    out.sort((a, b) => {
      let av = 0;
      let bv = 0;
      if (sortKey === "label") {
        return sortAsc ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
      }
      if (sortKey === "active") {
        av = a.activeUsers;
        bv = b.activeUsers;
      } else if (sortKey === "total") {
        av = a.totalUsers;
        bv = b.totalUsers;
      } else {
        av = a.pct;
        bv = b.pct;
      }
      if (av === bv) return a.label.localeCompare(b.label);
      return sortAsc ? av - bv : bv - av;
    });
    return out;
  }, [enriched, sortKey, sortAsc]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortAsc((s) => !s);
    else {
      setSortKey(k);
      // Default direction per column: alpha asc, numeric desc, engagement asc.
      setSortAsc(k === "label" || k === "engagement");
    }
  };

  const th = (k: SortKey, align: "left" | "right" = "right"): React.CSSProperties => ({
    cursor: "pointer",
    padding: "8px 10px",
    textAlign: align,
    fontSize: 12,
    color: sortKey === k ? "#3949ab" : "#6b7280",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  });

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{title}</div>
        {subtitle ? (
          <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div>
        ) : null}
      </div>

      {sorted.length === 0 ? (
        <div style={{ color: "#6b7280", padding: 24, textAlign: "center" }}>
          No agency engagement data for this date range.
        </div>
      ) : (
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th("label", "left")} onClick={() => onSort("label")}>
                Agency {sortKey === "label" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th style={th("active")} onClick={() => onSort("active")}>
                Active {sortKey === "active" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th style={th("total")} onClick={() => onSort("total")}>
                Total seats {sortKey === "total" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th style={th("engagement")} onClick={() => onSort("engagement")}>
                Engagement {sortKey === "engagement" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th
                style={{
                  padding: "8px 10px",
                  textAlign: "left",
                  fontSize: 12,
                  color: "#6b7280",
                  fontWeight: 700,
                  borderBottom: "1px solid #e5e7eb",
                  width: "30%",
                }}
              >
                {/* Inline horizontal bar column - no clickable sort. */}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id}>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", fontWeight: 600 }}>
                  {r.label}
                </td>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>
                  {formatInt(r.activeUsers)}
                </td>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", textAlign: "right", color: "#6b7280" }}>
                  {formatInt(r.totalUsers)}
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #f3f4f6",
                    textAlign: "right",
                    fontWeight: 700,
                    color: engagementColor(r.pct),
                  }}
                >
                  {r.pct}%
                </td>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6" }}>
                  <div
                    style={{
                      width: "100%",
                      height: 8,
                      borderRadius: 4,
                      background: "#f3f4f6",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${r.pct}%`,
                        height: "100%",
                        background: engagementColor(r.pct),
                      }}
                      title={`${r.activeUsers} active / ${r.totalUsers} seats (${r.dormant} dormant)`}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AgencyEngagement;
